import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetOwnMembershipQuery, useUpdateOwnDisplayNameMutation } from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";

const MAX_DISPLAY_NAME_LENGTH = 100;

export default function DisplayNameCard() {
  const { data: membership, isLoading } = useGetOwnMembershipQuery();
  const [updateDisplayName, { isLoading: isSaving }] = useUpdateOwnDisplayNameMutation();
  const [displayName, setDisplayName] = useState("");
  const realName = [membership?.user?.firstName, membership?.user?.lastName].filter(Boolean).join(" ");
  const savedDisplayName = membership?.displayName ?? "";
  const hasChanges = displayName.trim() !== savedDisplayName;

  async function save() {
    try {
      await updateDisplayName({ displayName: displayName.trim() || null }).unwrap();
      toast.success("Display name saved");
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Could not save your display name"));
    }
  }

  useEffect(() => {
    setDisplayName(savedDisplayName);
  }, [savedDisplayName]);

  if (isLoading) {
    return <Skeleton className="h-40 w-full" />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Display name</CardTitle>
        <CardDescription>
          How your teammates see you in this organisation, in transfers, teams, calls and tasks. Leave it empty to
          use your name.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(submitEvent) => {
            submitEvent.preventDefault();
            void save();
          }}
        >
          <div className="min-w-0 flex-1 space-y-2">
            <Label htmlFor="own-display-name">Display name</Label>
            <Input
              id="own-display-name"
              value={displayName}
              maxLength={MAX_DISPLAY_NAME_LENGTH}
              placeholder={realName}
              onChange={(changeEvent) => setDisplayName(changeEvent.target.value)}
            />
          </div>
          <Button type="submit" disabled={!hasChanges || isSaving}>
            Save
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
